/**
 * Meeting assistant settings section.
 *
 * Shows org-wide scheduling defaults as a form backed by CRM properties on
 * the `ahoy_org` entity.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { Button } from '@/lib/base/buttons/button';
import { UnsavedChangesModal } from '@/lib/base/overlays/unsaved-changes-modal';
import { PropertyInput } from '@/lib/crm/components/properties/PropertyInput';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import type { EntityTypeName } from '@/lib/crm/core/types';
import {
  type PropertyTypeSchema,
  usePropertySchema,
} from '@/lib/crm/hooks/usePropertySchema';
import { useSubmitEvents } from '@/lib/crm/query/mutations';
import { useCrmObject } from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import { ORG_SCHEDULING_DEFAULT_PROPERTIES } from '@/lib/scheduling/orgSchedulingDefaults';
import { useViewHealthMarker } from '@/lib/view-health';
import type { CrmObject } from '@/types/crm';

// ---------------------------------------------------------------------------
// Module-scope constants
// ---------------------------------------------------------------------------

// Shared with the booking-link create form, which reads these same properties
// back as its initial values. Keeping one list means a default added here
// cannot silently fail to reach new booking links.
const SCHEDULING_PROPERTIES: readonly string[] =
  ORG_SCHEDULING_DEFAULT_PROPERTIES;

const FIELD_LABELS: Record<string, string> = {
  ahoy_scheduling_default_working_hours: 'Working hours',
  ahoy_scheduling_default_buffer_before_minutes:
    'Buffer before meetings (minutes)',
  ahoy_scheduling_default_buffer_after_minutes:
    'Buffer after meetings (minutes)',
  ahoy_scheduling_default_min_notice_hours: 'Minimum notice required (hours)',
  ahoy_scheduling_default_max_days_ahead: 'Maximum days ahead',
  ahoy_scheduling_default_location_type: 'Default location',
};

const FIELD_DESCRIPTIONS: Record<string, string> = {
  ahoy_scheduling_default_working_hours:
    'The hours during which meetings can be scheduled.',
  ahoy_scheduling_default_buffer_before_minutes:
    'Time blocked before each meeting.',
  ahoy_scheduling_default_buffer_after_minutes:
    'Time blocked after each meeting.',
  ahoy_scheduling_default_min_notice_hours:
    'How far in advance a meeting must be booked.',
  ahoy_scheduling_default_max_days_ahead:
    'How many days ahead contacts can book.',
  ahoy_scheduling_default_location_type:
    'Where meetings take place by default.',
};

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function getPropertyValue(
  object: CrmObject | undefined,
  propName: string
): unknown {
  return object?.properties?.[propName]?.value ?? null;
}

// ---------------------------------------------------------------------------
// SchedulingPropertyField sub-component
// ---------------------------------------------------------------------------

interface SchedulingPropertyFieldProps {
  prop: string;
  propSchema: PropertyTypeSchema;
  formValue: string | number | object | null | undefined;
  handleFieldChange: (propName: string, value: unknown) => void;
  disabled: boolean;
}

function SchedulingPropertyField({
  prop,
  propSchema,
  formValue,
  handleFieldChange,
  disabled,
}: SchedulingPropertyFieldProps) {
  const handleChange = useCallback(
    (val: unknown) => {
      handleFieldChange(prop, val);
    },
    [handleFieldChange, prop]
  );

  return (
    <div>
      <label className='block text-sm font-medium text-fg-primary mb-1'>
        {FIELD_LABELS[prop]}
      </label>
      {FIELD_DESCRIPTIONS[prop] && (
        <p className='text-sm text-fg-tertiary mb-2'>
          {FIELD_DESCRIPTIONS[prop]}
        </p>
      )}
      <PropertyInput
        propertyType={propSchema}
        value={formValue}
        onChange={handleChange}
        disabled={disabled}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MeetingAssistantSection() {
  // --- All hooks first (no early returns before this block) ---
  const { currentOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const user = useUser();
  // Scheduling defaults live on the ahoy_org entity, whose ahoy_property_updated
  // permission is granted only by the ahoy_other_admin group. Derive a plain
  // boolean during render: usePermissions returns a fresh object with unstable
  // function identities each render, so hasPermission must never enter a dep array.
  const canEditSchedulingDefaults = hasPermission(
    'ahoy_org',
    'ahoy_property_updated'
  );
  const orgEntity = useCrmObject(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName,
    currentOrg?.entityId ?? undefined
  );
  const { schema, loading: schemaLoading } = usePropertySchema(
    currentOrg?.id ?? 0,
    'ahoy_org'
  );
  const { mutateAsync: submitEvents, isPending: isSaving } = useSubmitEvents(
    currentOrg?.id ?? 0,
    'ahoy_org' as EntityTypeName
  );
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [savedValues, setSavedValues] = useState<Record<string, unknown>>({});
  const initializedOrgRef = useRef<number | null>(null);

  useViewHealthMarker('settings-section-ready', {
    ready: Boolean(orgEntity.object),
  });

  // Sync CRM data → form state.
  // savedValues always tracks the canonical CRM state so hasChanges comparisons
  // stay accurate after WebSocket updates or refetches.
  // formValues is re-initialized when the org changes, but subsequent CRM updates
  // within the same org leave in-progress user edits untouched.
  useEffect(() => {
    if (orgEntity.object) {
      const values: Record<string, unknown> = {};
      for (const prop of SCHEDULING_PROPERTIES) {
        values[prop] = getPropertyValue(orgEntity.object, prop);
      }
      setSavedValues(values);
      const orgId = currentOrg?.id ?? null;
      if (initializedOrgRef.current !== orgId) {
        setFormValues(values);
        initializedOrgRef.current = orgId;
      }
    }
  }, [orgEntity.object, currentOrg?.id]);

  const handleFieldChange = useCallback((propName: string, value: unknown) => {
    setFormValues(prev => ({ ...prev, [propName]: value }));
  }, []);

  const hasChanges = useMemo(
    () =>
      SCHEDULING_PROPERTIES.some(
        prop =>
          JSON.stringify(formValues[prop]) !== JSON.stringify(savedValues[prop])
      ),
    [formValues, savedValues]
  );

  const handleSave = useCallback(async () => {
    if (!orgEntity.object || !canEditSchedulingDefaults) {
      return;
    }
    // Require an authenticated user. Submitting with an empty sourceId would
    // produce malformed events that can't be attributed to anyone.
    const userId = user?.id;
    if (userId === undefined || userId === null || userId === '') {
      toast.error('You must be signed in to save scheduling defaults');
      return;
    }

    const changedProperties: Record<string, unknown> = {};
    for (const prop of SCHEDULING_PROPERTIES) {
      if (
        JSON.stringify(formValues[prop]) !== JSON.stringify(savedValues[prop])
      ) {
        changedProperties[prop] = formValues[prop];
      }
    }

    if (Object.keys(changedProperties).length === 0) {
      return;
    }

    try {
      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(userId),
        startSeq: orgEntity.object.last_event_seq,
      });
      builder.propertiesUpdated(
        orgEntity.object.entity_id,
        'ahoy_org',
        changedProperties
      );
      await submitEvents({ events: builder.build() });
      toast.success('Scheduling defaults saved');
      setSavedValues({ ...formValues });
    } catch (err) {
      logger.error(
        'Failed to save scheduling defaults',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: currentOrg?.id }
      );
      toast.error("We couldn't save the scheduling defaults. Try again.");
      setFormValues({ ...savedValues });
    }
  }, [
    formValues,
    savedValues,
    orgEntity.object,
    submitEvents,
    user,
    currentOrg?.id,
    canEditSchedulingDefaults,
  ]);

  const handleDiscard = useCallback(() => {
    setFormValues({ ...savedValues });
  }, [savedValues]);

  const { modalProps } = useUnsavedChangesGuard({
    hasUnsavedChanges: hasChanges && canEditSchedulingDefaults,
    onDiscard: handleDiscard,
    onSave: handleSave,
  });

  // --- Conditional returns after all hooks ---
  if (!currentOrg?.entityId) {
    return null;
  }

  if (orgEntity.isLoading || schemaLoading) {
    return (
      <div className='space-y-8'>
        <div>
          <h2 className='text-lg font-semibold text-fg-primary'>
            Scheduling defaults
          </h2>
          <p className='text-sm text-fg-tertiary mt-0.5'>
            Default values for new meeting types in this org. These apply to
            meeting types created after this setting changes.
          </p>
        </div>
        <p className='text-sm text-fg-tertiary'>Loading…</p>
      </div>
    );
  }

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>
          Scheduling defaults
        </h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Default values for new meeting types in this org. These apply to
          meeting types created after this setting changes.
        </p>
        {!canEditSchedulingDefaults && (
          <p className='text-sm text-fg-tertiary mt-2'>
            Only account admins can change scheduling defaults. Ask an admin if
            these need updating.
          </p>
        )}
      </div>

      <div className='space-y-6'>
        {SCHEDULING_PROPERTIES.map(prop => {
          const propSchema = schema[prop];
          if (!propSchema) {
            return null;
          }
          return (
            <SchedulingPropertyField
              key={prop}
              prop={prop}
              propSchema={propSchema}
              formValue={
                formValues[prop] as string | number | object | null | undefined
              }
              handleFieldChange={handleFieldChange}
              disabled={isSaving || !canEditSchedulingDefaults}
            />
          );
        })}
      </div>

      <div className='flex justify-end'>
        <Button
          color='primary'
          size='md'
          onClick={handleSave}
          isDisabled={
            !hasChanges || isSaving || !user?.id || !canEditSchedulingDefaults
          }
          isLoading={isSaving}
        >
          Save changes
        </Button>
      </div>

      <UnsavedChangesModal {...modalProps} />
    </div>
  );
}
